import * as React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Collapse,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { NavLink, useLocation, useNavigate } from 'react-router';
import axios from '../api/axiosInstance';
import { buildMenuFromSettings, getMenuIcon } from '../menu/menuConfig';

export default function MenuContent({ collapsed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = React.useState({});
  const [menuConfig, setMenuConfig] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    axios.get('/api/settings/general')
      .then(({ data }) => {
        if (mounted) setMenuConfig(data?.config?.menu || null);
      })
      .catch(() => {
        if (mounted) setMenuConfig(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const menuItems = React.useMemo(() => buildMenuFromSettings(menuConfig || {}), [menuConfig]);
  const allowedItems = menuItems;

  React.useEffect(() => {
    const initialOpenMenus = {};
    allowedItems.forEach((item) => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some((subItem) => {
          const subPath = subItem.value ? `/${item.value}/${subItem.value}` : `/${item.value}`;
          return location.pathname === subPath;
        });
        initialOpenMenus[item.value] = hasActiveSubItem;
      }
    });
    setOpenMenus(initialOpenMenus);
  }, [location.pathname, allowedItems]);

  const toggleMenu = (menuKey) => {
    setOpenMenus((prev) => ({
      ...Object.fromEntries(Object.keys(prev).map((key) => [key, false])),
      [menuKey]: !prev[menuKey],
    }));
  };

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>
        {allowedItems.map((item, index) => {
          if (item.section) {
            if (collapsed) return null;
            return (
              <Typography
                key={`section-${item.key || index}`}
                variant="subtitle2"
                sx={{ pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }}
              >
                {item.section}
              </Typography>
            );
          }

          const mainPath = `/${item.value}`;
          const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;
          const isMainSelected = location.pathname === mainPath || location.pathname.startsWith(`${mainPath}/`);
          const isOpen = openMenus[item.value] || false;

          return (
            <div key={item.key || item.value || index}>
              <ListItem disablePadding sx={{ display: 'block' }}>
                <Tooltip title={collapsed ? item.text : ''} placement="right" arrow disableHoverListener={!collapsed}>
                <ListItemButton
                  onClick={() => {
                    if (hasSubItems && !collapsed) {
                      toggleMenu(item.value);
                    } else {
                      navigate(mainPath);
                    }
                  }}
                  selected={collapsed ? isMainSelected : (!hasSubItems && isMainSelected)}
                  sx={{ justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 1 : 2 }}
                >
                  <ListItemIcon sx={{ color: (collapsed ? isMainSelected : (!hasSubItems && isMainSelected)) ? 'primary.main' : 'inherit', minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                    {getMenuIcon(item.icon)}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.text} />}
                  {hasSubItems && !collapsed && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
                </Tooltip>
              </ListItem>

              {hasSubItems && !collapsed && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {item.subItems.map((subItem, subIndex) => {
                      const subPath = subItem.value ? `/${item.value}/${subItem.value}` : `/${item.value}`;
                      const isSubSelected = location.pathname === subPath;

                      return (
                        <NavLink
                          to={subPath}
                          key={subItem.key || subIndex}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <ListItem disablePadding>
                            <ListItemButton sx={{ pl: 4 }} selected={isSubSelected}>
                              <ListItemIcon sx={{ color: isSubSelected ? 'primary.main' : 'inherit' }}>
                                {getMenuIcon(subItem.icon)}
                              </ListItemIcon>
                              <ListItemText primary={subItem.text} />
                            </ListItemButton>
                          </ListItem>
                        </NavLink>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
    </Stack>
  );
}
